export const LOGIN_VARIANTS = {
    CHAMPSTPO: 'ChampsTPO',
    SKYTPO: 'SkyTPO'
};

/*
 * Canonical loan-program codes.
 *
 * The same product is branded differently in each company's Encompass
 * instance - what ChampsTPO calls ACTIVATOR, SkyTPO calls SUNNY - and some
 * products exist in only one of them. So the application never reasons about
 * a product by its brand name. Every comparison, rule key, dropdown value and
 * lookup table uses one of these codes; the brand name is resolved from the
 * org's `loanPrograms` map at the two edges only:
 *
 *   - reading  a value out of Encompass  -> getCanonicalLoanProgram()
 *   - writing  a value back to Encompass -> getLoanProgramValue()
 *   - showing  a value to the user       -> getLoanProgramValue()
 *
 * Those edges live in src/utils/loanProgramUtil.js, which binds them to the
 * org resolved from REACT_APP_ORGANIZATION. Adding a company means adding a
 * `loanPrograms` block below and nothing else.
 *
 * The codes are deliberately opaque. A name-shaped canonical key (ACTIVATOR)
 * would quietly re-privilege one brand and invite direct string comparison
 * against Encompass data, which is the bug this indirection exists to prevent.
 */
export const LOAN_PROGRAM_CODES = {
    PROGRAM_001: 'PROGRAM_001',
    PROGRAM_002: 'PROGRAM_002',
    PROGRAM_003: 'PROGRAM_003',
    PROGRAM_004: 'PROGRAM_004',
    PROGRAM_005: 'PROGRAM_005',
    PROGRAM_006: 'PROGRAM_006',
    PROGRAM_007: 'PROGRAM_007',
    PROGRAM_008: 'PROGRAM_008',
    PROGRAM_009: 'PROGRAM_009',
    PROGRAM_010: 'PROGRAM_010',
    PROGRAM_011: 'PROGRAM_011',
    PROGRAM_012: 'PROGRAM_012',
    PROGRAM_013: 'PROGRAM_013'
};

/*
 * Per-company product names, keyed by canonical code.
 *
 * `null` means the product does not exist in that company's Encompass
 * instance: it is filtered out of every dropdown, and a canonical code that
 * maps to null resolves to no display name. Keep the keys in PROGRAM_001..013
 * order - getLoanProgramOptions() preserves insertion order, so this is the
 * default option order for any list not spelled out in src/data/**.
 *
 *   code          ChampsTPO                   SkyTPO
 *   PROGRAM_001   ALLY                        -
 *   PROGRAM_002   ACCELERATOR                 BREEZY
 *   PROGRAM_003   ACCELERATOR INCOME          BREEZY INCOME
 *   PROGRAM_004   ACTIVATOR                   SUNNY
 *   PROGRAM_005   ACCELERATOR ITIN            BREEZY ITIN
 *   PROGRAM_006   ACTIVATOR ITIN              SUNNY ITIN
 *   PROGRAM_007   AMBASSADOR                  PASSPORT
 *   PROGRAM_008   SUPER JUMBO                 STARRY
 *   PROGRAM_009   ACTIVATOR PRIME             SUNNY SELECT
 *   PROGRAM_010   ACCELERATOR EQUITY FIRST    CLOUDY
 *   PROGRAM_011   ALLY EQUITY FIRST           -
 *   PROGRAM_012   -                           SKY PLUS
 *   PROGRAM_013   ACTIVATOR EQUITY FIRST      -
 */
const CHAMPS_LOAN_PROGRAMS = {
    PROGRAM_001: 'ALLY',
    PROGRAM_002: 'ACCELERATOR',
    PROGRAM_003: 'ACCELERATOR INCOME',
    PROGRAM_004: 'ACTIVATOR',
    PROGRAM_005: 'ACCELERATOR ITIN',
    PROGRAM_006: 'ACTIVATOR ITIN',
    PROGRAM_007: 'AMBASSADOR',
    PROGRAM_008: 'SUPER JUMBO',
    PROGRAM_009: 'ACTIVATOR PRIME',
    PROGRAM_010: 'ACCELERATOR EQUITY FIRST',
    PROGRAM_011: 'ALLY EQUITY FIRST',
    // Not available for this Encompass instance.
    PROGRAM_012: null,
    PROGRAM_013: 'ACTIVATOR EQUITY FIRST'
};

const SKY_LOAN_PROGRAMS = {
    // Not available for this Encompass instance.
    PROGRAM_001: null,
    PROGRAM_002: 'BREEZY',
    PROGRAM_003: 'BREEZY INCOME',
    PROGRAM_004: 'SUNNY',
    PROGRAM_005: 'BREEZY ITIN',
    PROGRAM_006: 'SUNNY ITIN',
    PROGRAM_007: 'PASSPORT',
    PROGRAM_008: 'STARRY',
    PROGRAM_009: 'SUNNY SELECT',
    PROGRAM_010: 'CLOUDY',
    // Not available for this Encompass instance.
    PROGRAM_011: null,
    PROGRAM_012: 'SKY PLUS',
    // Not available for this Encompass instance.
    PROGRAM_013: null
};

/*
 * Brand palettes.
 *
 * Keys ARE the CSS custom property names - applyOrgTheme() writes them straight
 * onto <html>, so adding a new company means adding a `theme` block here and
 * nothing else. Generic fallbacks for every token live in
 * src/styles/genericTheme.css; a theme only needs to override what differs.
 *
 * Consume them as var(--brand-primary) etc. Never hardcode a brand hex in a
 * component or a component stylesheet.
 */
const CHAMPS_THEME = {
    // Champions Funding orange.
    '--brand-primary': '#f48527',
    '--brand-primary-hover': '#dc7418',
    '--brand-primary-active': '#c0640f',
    '--brand-primary-contrast': '#ffffff',
    '--brand-primary-soft': '#fdf2e8',
    '--brand-primary-softer': '#fef8f2',
    '--brand-primary-border': '#f8b67d',
    // Borrower-name pill in the loan header. Champions keeps the light blue the
    // markup used to hardcode as Tailwind `bg-blue-200`, rather than taking the
    // orange primary tint. SkyTPO/MMC has no override, so it falls through to
    // the generic default and picks up MMC's light green.
    '--brand-highlight-bg': '#bfdbfe',
    // Champions Funding pink.
    '--brand-accent': '#ff0f63',
    '--brand-accent-hover': '#e00a56',
    '--brand-accent-active': '#c40849',
    '--brand-accent-contrast': '#ffffff',
    '--brand-accent-soft': '#ffe4ed',
    '--brand-accent-border': '#f16191',
    '--brand-accent-muted': '#f14e84',
    '--brand-btn-bg': 'var(--brand-accent)',
    '--brand-btn-fg': 'var(--brand-accent-contrast)',
    '--brand-btn-hover-bg': 'var(--brand-accent-hover)',
    /*
     * Left navigation rail. Champions keeps its established look, which is NOT
     * derived from the orange primary: a slate rail, light blue hover/selected
     * pills, an orange completed disc with a white check, and grey pending
     * outlines. These are spelled out rather than left to the generic defaults
     * in src/styles/genericTheme.css precisely so that changing those defaults
     * for a new company cannot move Champions.
     */
    '--brand-sidebar-bg': '#415f78',
    '--brand-sidebar-hover-bg': '#ebf8ff',
    '--brand-sidebar-hover-fg': '#1e3a8a',
    '--brand-sidebar-active-bg': '#ebf8ff',
    // Completed step: orange disc, white check. Pending: grey-400 outline.
    '--brand-sidebar-icon-done': '#f48527',
    '--brand-sidebar-icon-done-fg': '#ffffff',
    '--brand-sidebar-muted-fg': '#9ca3af',
    // Submenu rows keep the light blue pills the markup used to hardcode as
    // Tailwind `bg-blue-100 text-blue-900`, rather than the orange primary ramp.
    '--brand-sidebar-subitem-hover-bg': '#dbeafe',
    '--brand-sidebar-subitem-hover-fg': '#1e3a8a',
    '--brand-sidebar-subitem-active-bg': '#dbeafe',
    '--brand-sidebar-subitem-active-fg': '#1e3a8a',
    '--brand-consumer-hover-bg': '#f14e844d',
};

/*
 * MMC Lending teal.
 *
 * Surfaces use the muted slate-teal sampled from skytpo.com's own chrome;
 * CTAs and links use the saturated Tailwind teal-700/800/900 that the MMC
 * header already shipped in src/components/login/MMCLendingHeader.jsx uses
 * (`bg-teal-700 hover:bg-teal-800`). Keep the two in step: if that component's
 * teals change, change these with them.
 */
const SKY_THEME = {
    // Surfaces and chrome. White on #365355 is 5.8:1, so it clears AA.
    '--org-primary': '#295E9B',
    '--brand-primary': '#90CAF8',
    '--brand-primary-hover': '#134e4a',
    '--brand-primary-active': '#042f2e',
    '--brand-primary-contrast': '#ffffff',
    '--brand-highlight-bg': "transparent",
    '--brand-highlight-fg': "#0E2A47",
    // teal-100 / teal-50. Soft has to read as teal, not as off-white - it is
    // the fill behind the borrower name in the loan header.
    '--brand-primary-soft': '#DCEEFD',
    '--brand-primary-softer': '#f0fdfa',
    '--brand-primary-border': '#90CAF8',
    '--brand-sidebar-icon-done': '#90CAF8',
    '--brand-sidebar-icon-done-fg': '#ffffff',
    '--brand-sidebar-muted-fg': '#DCEEFD',
    // CTAs and links: teal-700 / 800 / 900, a step more saturated than the
    // chrome so buttons lift off it while still clearing 4.5:1 on white.
    // teal-600, which the header uses for link *hover*, is only 3.7:1 and fails
    // AA as a resting colour, so it is deliberately not used here.
    '--brand-accent': 'var(--org-primary)',
    '--brand-accent-hover': '#115e59',
    '--brand-accent-active': '#134e4a',
    '--brand-accent-contrast': '#ffffff',
    '--brand-accent-soft': '#ccfbf1',
    '--brand-accent-border': '#5eead4',
    '--brand-accent-muted': '#14b8a6',
    /*
     * Filled buttons. MMC's CTAs are the deep teal chrome rather than the
     * saturated accent the generic defaults use, darkening to the bottom of the
     * ramp on hover. Referencing the ramp rather than repeating #365355 /
     * #042f2e keeps the buttons in step if the teal above is ever retuned. The
     * transition rides on .btn-primary / .em-btn-secondary in src/common.css, so
     * it applies to every brand.
     */
    '--brand-btn-bg': 'var(--org-primary)',
    '--brand-btn-fg': 'var(--brand-primary-contrast)',
    '--brand-btn-hover-bg': 'var(--brand-primary)',
    /*
     * Tertiary buttons darken on hover like every other MMC button instead of
     * inverting to the white surface the generic default uses - so Preview / Order
     * on the validation pages match Save and Cancel beside them.
     */
    '--brand-btn-tertiary-hover-bg': 'var(--org-primary)',
    '--brand-btn-tertiary-hover-fg': 'var(--brand-btn-fg)',
    '--brand-btn-tertiary-hover-border': 'var(--org-primary)',
    /*
     * Left navigation rail. Beyond the rail colour and the submenu pills, the
     * sidebar tokens are left to the generic defaults in
     * src/styles/genericTheme.css, which derive from the rail: a white-alpha
     * hover wash, a white selected pill with rail-coloured ink, a white
     * completed disc with a teal check, and translucent-white pending outlines.
     */
    '--brand-sidebar-bg': '#295E9B',
    /*
     * Hovered row: a pale grey pill carrying the deep teal as ink. The icon needs
     * no rule of its own - .brand-sidebar-icon is `color: inherit`, so it tracks
     * the row colour along with the label.
     */
    '--brand-sidebar-hover-fg': '#295E9B',
    '--brand-sidebar-hover-bg': '#ebf6fff5',
    '--brand-sidebar-active-bg': 'var(--brand-sidebar-hover-bg)',
    '--brand-sidebar-active-fg': 'var(--brand-sidebar-hover-fg)',
    /*
     * Pending step markers - the outlined circle and the number inside it - flip
     * to the deep teal only while the row carries a light pill, i.e. hovered or
     * selected. At rest they keep the generic translucent white, which is what
     * stays legible against the rail. Both halves of the marker come off one
     * token per state: .brand-sidebar-step uses it for `border` and `color` alike.
     */
    '--brand-sidebar-muted-hover-fg': '#295E9B',
    '--brand-sidebar-muted-active-fg': 'var(--brand-sidebar-muted-hover-fg)',
    '--brand-sidebar-icon-done-hover-border': 'var(--brand-primary)',
    '--brand-sidebar-icon-done-hover': '#295E9B',
    '--brand-sidebar-icon-done-active-border': 'var(--org-primary)',
    /*
     * Submenu pills in MMC's light green (teal-100) with deep teal ink - the
     * counterpart to Champions' light blue pills. Only the selected pair is set:
     * genericTheme.css points the hover pair at these, so hover and selected
     * match. Referencing the ramp rather than repeating hexes keeps the pill in
     * step if the teal above is ever retuned.
     */
    '--brand-sidebar-subitem-active-bg': 'var(--brand-primary-soft)',
    '--brand-sidebar-subitem-active-fg': 'var(--brand-primary-active)',
    '--brand-consumer-hover-bg': '#5c7999',
};

/*
 * Which channel an exception-form loan program belongs to. BUSINESS and
 * CONSUMER match the values of CX.LOAN.TYPE; BOTH is shown under either.
 */
export const EXCEPTION_LOAN_CHANNELS = {
    BUSINESS: 'Business',
    CONSUMER: 'Consumer',
    BOTH: 'Both'
};

/*
 * Guideline exception form - Pre-Submission Loan Program
 * (CX.EXC.PRE.REQ.LOANPROGRAM).
 *
 * These are rate-sheet *variants* - doc type and occupancy combined with a
 * product - not the plain product names in `loanPrograms` above, so they are
 * spelled out per company rather than derived from the canonical codes. Brand
 * names differ between the two (Activator/Sunny, Accelerator/Breezy) while the
 * generic descriptors do not (ITIN, Foreign National, Super Jumbo, Equity
 * First, DSCR 5-8 units).
 *
 * `value` is written verbatim into the Encompass field, so treat these strings
 * as data: changing one orphans every loan already saved with the old text.
 */
const CHAMPS_EXCEPTION_LOAN_PROGRAMS = [
    { value: 'DSCR 1-4 units (Accelerator)', label: 'DSCR 1-4 units (Accelerator)', key: EXCEPTION_LOAN_CHANNELS.BUSINESS },
    { value: 'Alt Doc Primary/2nd home(Activator)', label: 'Alt Doc Primary/2nd home(Activator)', key: EXCEPTION_LOAN_CHANNELS.CONSUMER },
    { value: 'Full Doc Primary/2nd home (Activator)', label: 'Full Doc Primary/2nd home (Activator)', key: EXCEPTION_LOAN_CHANNELS.CONSUMER },
    { value: 'Alt Doc NOO (Accelerator)', label: 'Alt Doc NOO (Accelerator)', key: EXCEPTION_LOAN_CHANNELS.BUSINESS },
    { value: 'Full Doc NOO (Accelerator)', label: 'Full Doc NOO (Accelerator)', key: EXCEPTION_LOAN_CHANNELS.BUSINESS },
    { value: 'ITIN Primary/2nd Home', label: 'ITIN Primary/2nd Home', key: EXCEPTION_LOAN_CHANNELS.CONSUMER },
    { value: 'ITIN DSCR', label: 'ITIN DSCR', key: EXCEPTION_LOAN_CHANNELS.BUSINESS },
    { value: 'Foreign National DSCR', label: 'Foreign National DSCR', key: EXCEPTION_LOAN_CHANNELS.BUSINESS },
    { value: 'Foreign National Income', label: 'Foreign National Income', key: EXCEPTION_LOAN_CHANNELS.BOTH },
    { value: 'Super Jumbo', label: 'Super Jumbo', key: EXCEPTION_LOAN_CHANNELS.CONSUMER },
    { value: 'No Income/Employment/DTI (Ally)', label: 'No Income/Employment/DTI (Ally)', key: EXCEPTION_LOAN_CHANNELS.CONSUMER },
    { value: 'DSCR 5-8 units', label: 'DSCR 5-8 units', key: EXCEPTION_LOAN_CHANNELS.BUSINESS },
    { value: 'DSCR 1-4 units (Equity First)', label: 'DSCR 1-4 units (Equity First)', key: EXCEPTION_LOAN_CHANNELS.BUSINESS },
    { value: 'No Income/Employment/DTI (Ally Equity First)', label: 'No Income/Employment/DTI (Ally Equity First)', key: EXCEPTION_LOAN_CHANNELS.CONSUMER },
    { value: '1 Year Alt Doc (Activator Equity First)', label: '1 Year Alt Doc (Activator Equity First)', key: EXCEPTION_LOAN_CHANNELS.CONSUMER }
];

/*
 * Ordered to mirror the ChampsTPO list above so the two read alike. The Ally
 * rows have no SkyTPO equivalent and are simply absent; Sunny Select and the
 * three Closed-End Seconds rows are SkyTPO-only and come last.
 */
const SKY_EXCEPTION_LOAN_PROGRAMS = [
    { value: 'DSCR 1-4 units (Breezy)', label: 'DSCR 1-4 units (Breezy)', key: EXCEPTION_LOAN_CHANNELS.BUSINESS },
    { value: 'Alt Doc Primary/2nd home (Sunny)', label: 'Alt Doc Primary/2nd home (Sunny)', key: EXCEPTION_LOAN_CHANNELS.CONSUMER },
    { value: 'Full Doc Primary/2nd home (Sunny)', label: 'Full Doc Primary/2nd home (Sunny)', key: EXCEPTION_LOAN_CHANNELS.CONSUMER },
    { value: 'Alt Doc NOO (Breezy)', label: 'Alt Doc NOO (Breezy)', key: EXCEPTION_LOAN_CHANNELS.BUSINESS },
    { value: 'Full Doc NOO (Breezy)', label: 'Full Doc NOO (Breezy)', key: EXCEPTION_LOAN_CHANNELS.BUSINESS },
    { value: 'ITIN Primary/2nd Home', label: 'ITIN Primary/2nd Home', key: EXCEPTION_LOAN_CHANNELS.CONSUMER },
    { value: 'ITIN DSCR', label: 'ITIN DSCR', key: EXCEPTION_LOAN_CHANNELS.BUSINESS },
    { value: 'Foreign National DSCR', label: 'Foreign National DSCR', key: EXCEPTION_LOAN_CHANNELS.BUSINESS },
    { value: 'Foreign National Income', label: 'Foreign National Income', key: EXCEPTION_LOAN_CHANNELS.BOTH },
    { value: 'Super Jumbo', label: 'Super Jumbo', key: EXCEPTION_LOAN_CHANNELS.CONSUMER },
    { value: 'DSCR 5-8 units', label: 'DSCR 5-8 units', key: EXCEPTION_LOAN_CHANNELS.BUSINESS },
    { value: 'DSCR 1-4 units (Equity First)', label: 'DSCR 1-4 units (Equity First)', key: EXCEPTION_LOAN_CHANNELS.BUSINESS },
    { value: 'Bank Statement Select Primary/2nd home (Sunny Select)', label: 'Bank Statement Select Primary/2nd home (Sunny Select)', key: EXCEPTION_LOAN_CHANNELS.CONSUMER },
    { value: 'Full & Alt Doc Closed-End Seconds (Sky Plus)', label: 'Full & Alt Doc Closed-End Seconds (Sky Plus)', key: EXCEPTION_LOAN_CHANNELS.BOTH },
    { value: 'Full & Alt Doc Closed-End Seconds (Sky Plus Select)', label: 'Full & Alt Doc Closed-End Seconds (Sky Plus Select)', key: EXCEPTION_LOAN_CHANNELS.BOTH },
    { value: 'DSCR Closed-End Seconds (Sky Plus)', label: 'DSCR Closed-End Seconds (Sky Plus)', key: EXCEPTION_LOAN_CHANNELS.BUSINESS }
];

/*
 * Guidelines & Matrices page (src/components/guidelinematrices/matrices.js).
 *
 * Two independent columns - `matrices` and `guidelines` - each a list of
 * titled sections. Every item carries its own `url`, so the same product can
 * point at a rate matrix in one column and at a guideline document in the
 * other. The page used to key its links off a shared name -> url map, which
 * silently handed Super Jumbo the same PDF in both columns and forced a
 * "(Guidelines)" suffix onto labels just to keep two keys apart.
 *
 * Item shapes:
 *   { label, url }            -> a linked row
 *   { label, url, subItems }  -> a linked row with a bulleted list beneath it
 *   { label, subItems }       -> an unlinked bold heading with a plain list
 *
 * An empty or missing list hides that column, and a company with neither gets
 * a "not published yet" note instead of two empty cards - so a new company can
 * be registered before its documents exist.
 */
const CHAMPS_GUIDELINE_MATRICES = {
    matrices: [
        {
            title: 'Primary Residence & Second Homes',
            items: [
                { label: 'No Income/Employment Verification - Ally Equity First', url: 'https://21505619.fs1.hubspotusercontent-na1.net/hubfs/21505619/Website%20-%20Guidelines%20(2025)/Ally%20Equity%20First.pdf' },
                { label: 'No Income/Employment Verification - Ally', url: 'https://21505619.fs1.hubspotusercontent-na1.net/hubfs/21505619/Website - Guidelines (2025)/Ally No Ratio.pdf' },
                { label: 'Alt-Doc - Activator Equity First', url: 'https://21505619.fs1.hubspotusercontent-na1.net/hubfs/21505619/Website%20-%20Guidelines%20(2025)/Activator%20Equity%20First%2010-05-2026.pdf' },
                { label: 'Alt-Doc - Activator', url: 'https://21505619.fs1.hubspotusercontent-na1.net/hubfs/21505619/Website - Guidelines (2025)/Accelerator-Activator Alt Doc.pdf' },
                { label: 'Full-Doc - Activator', url: 'https://21505619.fs1.hubspotusercontent-na1.net/hubfs/21505619/Website - Guidelines (2025)/Accelerator-Activator Full Doc.pdf' },
                { label: 'ITIN Full/Alt-Doc - Activator', url: 'https://21505619.fs1.hubspotusercontent-na1.net/hubfs/21505619/Website - Guidelines (2025)/ITIN Activator Alt Doc.pdf' },
                { label: 'Foreign National Full/Alt-Doc - Ambassador', url: 'https://21505619.fs1.hubspotusercontent-na1.net/hubfs/21505619/Website - Guidelines (2025)/FN-Ambassador-2nd-Full-Alt.pdf' },
                { label: 'Super Jumbo Full/Alt-Doc', url: 'https://21505619.fs1.hubspotusercontent-na1.net/hubfs/21505619/Website - Guidelines (2025)/Super Jumbo.pdf' }
            ]
        },
        {
            title: 'Investment Properties',
            items: [
                { label: 'DSCR - DSCR Equity First', url: 'https://21505619.fs1.hubspotusercontent-na1.net/hubfs/21505619/Website%20-%20Guidelines%20(2025)/Equity%20First.pdf' },
                { label: 'DSCR 1-4 Unit - Accelerator', url: 'https://21505619.fs1.hubspotusercontent-na1.net/hubfs/21505619/Website - Guidelines (2025)/Accelerator 1-4 Units.pdf' },
                { label: 'DSCR 5-8 Unit - Accelerator', url: 'https://21505619.fs1.hubspotusercontent-na1.net/hubfs/21505619/Website - Guidelines (2025)/Accelerator 5-8 Units.pdf' },
                { label: 'Alt-Doc - Accelerator', url: 'https://21505619.fs1.hubspotusercontent-na1.net/hubfs/21505619/Website - Guidelines (2025)/Accelerator-Activator Alt Doc.pdf' },
                { label: 'Full-Doc - Accelerator', url: 'https://21505619.fs1.hubspotusercontent-na1.net/hubfs/21505619/Website - Guidelines (2025)/Accelerator-Activator Full Doc.pdf' },
                { label: 'ITIN DSCR - Accelerator', url: 'https://21505619.fs1.hubspotusercontent-na1.net/hubfs/21505619/Website - Guidelines (2025)/ITIN DSCR.pdf' },
                { label: 'Foreign National DSCR - Ambassador', url: 'https://21505619.fs1.hubspotusercontent-na1.net/hubfs/21505619/Website - Guidelines (2025)/FN-Ambassador-DSCR.pdf' },
                { label: 'Foreign National Full/Alt-Doc - Ambassador', url: 'https://21505619.fs1.hubspotusercontent-na1.net/hubfs/21505619/Website - Guidelines (2025)/FN-Ambassador-2nd-Full-Alt.pdf' }
            ]
        }
    ],
    guidelines: [
        {
            title: 'Primary Residence, Second Home, & Investment Properties',
            items: [
                {
                    label: 'Main Guidelines',
                    url: 'https://21505619.fs1.hubspotusercontent-na1.net/hubfs/21505619/Website - Guidelines (2025)/Main Guidelines.pdf',
                    subItems: [
                        'Full/Alt-Doc',
                        'DSCR 1-4 Units',
                        'DSCR 5-8 Units',
                        'ITIN Full/Alt-Doc & DSCR',
                        'Foreign National Full/Alt-Doc & DSCR'
                    ]
                },
                { label: 'No Income/Employment Verification - Ally Equity First', url: 'https://21505619.fs1.hubspotusercontent-na1.net/hubfs/21505619/Website%20-%20Guidelines%20(2025)/Ally%20Equity%20First%20No%20Ratio%20Guidelines.pdf' },
                { label: 'No Income/Employment Verification - Ally', url: 'https://21505619.fs1.hubspotusercontent-na1.net/hubfs/21505619/Website - Guidelines (2025)/Ally No Ratio Guidelines.pdf' },
                { label: 'Alt-Doc - Activator Equity First', url: 'https://21505619.fs1.hubspotusercontent-na1.net/hubfs/21505619/Website%20-%20Guidelines%20(2025)/Activator%20Equity%20First%20Guidelines.pdf' },
                { label: 'Super Jumbo Full/Alt-Doc', url: 'https://21505619.fs1.hubspotusercontent-na1.net/hubfs/21505619/Website - Guidelines (2025)/Super Jumbo Guidelines.pdf' },
                { label: 'DSCR - DSCR Equity First', url: 'https://21505619.fs1.hubspotusercontent-na1.net/hubfs/21505619/Website%20-%20Guidelines%20(2025)/Equity%20First%20Guidelines%2006-08-2026.pdf' },
            ]
        }
    ]
};

/*
 * SkyTPO publishes one document per product rather than a single main
 * guideline, so its `matrices` column is four sections keyed by occupancy with
 * the product named in each label - the same "<doc type> - <product>" reading
 * order Champions' labels use, so the two pages scan alike.
 */
const SKY_GUIDELINE_MATRICES = {
    guidelines: [
        {
            title: 'Primary Residence & Second Homes',
            items: [
                { label: 'Full Documentation - Sunny', url: 'https://skytpo.com/docs/sunny/full-documentation/guidelines' },
                { label: 'Alternative Documentation - Sunny', url: 'https://skytpo.com/docs/sunny/alternative-documentation/guidelines' },
                { label: 'Bank Statement Select - Sunny', url: 'https://skytpo.com/docs/sunny/bank-statement-select/guidelines' },
                { label: 'ITIN - Sunny', url: 'https://skytpo.com/docs/sunny/itin/guidelines' }
            ]
        },
        {
            title: 'Investment Properties',
            items: [
                { label: 'Full Documentation - Breezy', url: 'https://skytpo.com/docs/breezy/full-documentation/guidelines' },
                { label: 'Alternative Documentation - Breezy', url: 'https://skytpo.com/docs/breezy/alternative-documentation/guidelines' },
                { label: 'DSCR 1-4 Unit - Breezy', url: 'https://skytpo.com/docs/breezy/dscr-1-4-unit/guidelines' },
                { label: 'DSCR 5-8 Unit - Breezy', url: 'https://skytpo.com/docs/breezy/dscr-5-8-unit/guidelines' },
                { label: 'ITIN DSCR 1-4 Unit - Breezy', url: 'https://skytpo.com/docs/breezy/itin-dscr-1-4-unit/guidelines' },
                { label: 'Equity DSCR - Cloudy', url: 'https://skytpo.com/docs/cloudy/equity-dscr/guidelines' },
                { label: 'DSCR 1-4 Unit - Passport', url: 'https://skytpo.com/docs/passport/dscr-1-4-unit/guidelines' },
                { label: 'Full & Alternative Documentation - Passport', url: 'https://skytpo.com/docs/passport/full-and-alternative-documentation/guidelines' }
            ]
        },
        {
            title: 'Primary, Second Home & Investment Properties',
            items: [
                { label: 'Full & Alternative Documentation - Sky Plus', url: 'https://skytpo.com/docs/sky-plus/full-and-alternative-documentation/guidelines' },
                { label: 'Full Documentation Select - Sky Plus', url: 'https://skytpo.com/docs/sky-plus/full-documentation-select/guidelines' },
                { label: 'DSCR - Sky Plus', url: 'https://skytpo.com/docs/sky-plus/dscr/guidelines' }
            ]
        },
        {
            title: 'Primary Home',
            items: [
                { label: 'Full & Alternative Documentation - Starry', url: 'https://skytpo.com/docs/starry/full-and-alternative-documentation/guidelines' }
            ]
        }
    ],
    matrices: [
        {
            title: 'Primary Residence & Second Homes',
            items: [
                { label: 'Full Documentation - Sunny', url: 'https://skytpo.com/docs/sunny/full-documentation/matrix' },
                { label: 'Alternative Documentation - Sunny', url: 'https://skytpo.com/docs/sunny/alternative-documentation/matrix' },
                { label: 'Bank Statement Select - Sunny', url: 'https://skytpo.com/docs/sunny/bank-statement-select/matrix' },
                { label: 'ITIN - Sunny', url: 'https://skytpo.com/docs/sunny/itin/matrix' }
            ]
        },
        {
            title: 'Investment Properties',
            items: [
                { label: 'Full Documentation - Breezy', url: 'https://skytpo.com/docs/breezy/full-documentation/matrix' },
                { label: 'Alternative Documentation - Breezy', url: 'https://skytpo.com/docs/breezy/alternative-documentation/matrix' },
                { label: 'DSCR 1-4 Unit - Breezy', url: 'https://skytpo.com/docs/breezy/dscr-1-4-unit/matrix' },
                { label: 'DSCR 5-8 Unit - Breezy', url: 'https://skytpo.com/docs/breezy/dscr-5-8-unit/matrix' },
                { label: 'ITIN DSCR 1-4 Unit - Breezy', url: 'https://skytpo.com/docs/breezy/itin-dscr-1-4-unit/matrix' },
                { label: 'Equity DSCR - Cloudy', url: 'https://skytpo.com/docs/cloudy/equity-dscr/matrix' },
                { label: 'DSCR 1-4 Unit - Passport', url: 'https://skytpo.com/docs/passport/dscr-1-4-unit/matrix' },
                { label: 'Full & Alternative Documentation - Passport', url: 'https://skytpo.com/docs/passport/full-and-alternative-documentation/matrix' }
            ]
        },
        {
            title: 'Primary, Second Home & Investment Properties',
            items: [
                { label: 'Full & Alternative Documentation - Sky Plus', url: 'https://skytpo.com/docs/sky-plus/full-and-alternative-documentation/matrix' },
                { label: 'Full Documentation Select - Sky Plus', url: 'https://skytpo.com/docs/sky-plus/full-documentation-select/matrix' },
                { label: 'DSCR - Sky Plus', url: 'https://skytpo.com/docs/sky-plus/dscr/matrix' }
            ]
        },
        {
            title: 'Primary Home',
            items: [
                { label: 'Full & Alternative Documentation - Starry', url: 'https://skytpo.com/docs/starry/full-and-alternative-documentation/matrix' }
            ]
        }
    ]
};

// Keyed by the value of REACT_APP_ORGANIZATION (matched case-insensitively).
export const ORGANIZATIONS = {
    SkyTPO: {
        id: 'SkyTPO',
        showHeaderLtv: true,
        logo: '/content/Images/mmc/MMC_Lending_Logo.png',
        footerLogo: '/content/Images/mmc/SKYTPO_logo_transparent.png',
        favicon: '/content/Images/mmc/skytpo-favicon-32x32.png',
        layoutBackground: '/content/Images/mmc/SkyPORT_Background.png',
        theme: SKY_THEME,
        loanPrograms: SKY_LOAN_PROGRAMS,
        // Products listed but not selectable in the Additional Info dropdown.
        // SkyTPO brokers can pick every product it carries.
        disabledLoanPrograms: [],
        // Field 1401 (Loan Program) names that block Send/Request Disclosures and Submit Loan.
        disabledLoanProgramNames: [],
        // MISMO import: drop Estimated Net Monthly Rental Income
        // (URLA.X81 / rentalEstimatedNetMonthlyRentAmount) when the file has no REO properties.
        clearImportedRentalIncomeWithoutReo: true,
        hiddenAdditionalInfoFields: ['CX.CDFI.ELIGIBLE'],
        // SkyTPO doesn't support "No Income/Employment/DTI" (value '21') on
        // Wholesale; Tax Returns ('9018') takes its place as the default.
        hiddenIncomeTypes: ['21'],
        defaultIncomeType: '9018',
        showAchCheckbox: false,
        exceptionLoanPrograms: SKY_EXCEPTION_LOAN_PROGRAMS,
        guidelineMatrices: SKY_GUIDELINE_MATRICES,
        /*
         * Rate-comparison sheet offered from Quick Price when several
         * PROGRAM_009 programs come back. null hides the Compare button
         * entirely - better than showing brokers another company's artwork.
         * TODO: no SkyTPO equivalent supplied yet.
         */
        quickPriceActivatorPrimeComparisonImage: null,
        // SkyTPO does not offer guideline exceptions from the Quick Price
        // ineligible result - the note (and its link) is hidden entirely.
        // The message below already points the broker to their AE instead.
        showIneligibleExceptionNote: false,
        ineligibleMessage: 'This scenario falls outside our standard guidelines, but exceptions are very welcome at Sky. Please contact your Account Executive to review the file and explore a path to approval.',
        companyName: 'Sky TPO',
        shortName: 'SKY',
        tpoName: 'SkyTPO',
        allContacts: [
            {
                role: "Scenarios Desk",
                userName: "",
                email: "scenarios@skytpo.com",
                phone: "",
            },
            {
                role: "Client Approval",
                userName: "",
                email: "clientapproval@skytpo.com",
                phone: "",
            }
        ],
        allCorrContacts: [
            {
                role: "Scenarios Desk",
                userName: "",
                email: "corr.scenarios@skytpo.com",
                phone: "",
            },
            {
                role: "Client Support",
                userName: "",
                email: "corr.support@skytpo.com",
                phone: "",
            }
        ],
        emails: {
            clientApproval: 'clientapproval@skytpo.com',
        },
        links: {
            exceptionRequest: 'https://www.skytpo.com/exception-request',
            // Internal portal the logo points at for employee sessions.
            // TODO: no SkyTPO equivalent supplied yet - left blank so the logo href is untouched.
            employeePortal: '',
        },
        channelSupport: {
            wholesale: {
                title: 'Wholesale Support',
                phone: '888-210-9881',
                email: 'scenarios@skytpo.com',
            },
            correspondent: {
                title: 'Correspondent Support',
                phone: '888-503-7677',
                email: 'corr.support@skytpo.com',
            },
        },
        exceptionReqMsg: "This will submit an Exception Request Form to SKY. You may be asked for the credit report and additional relevant documentation such as Asset Statements, Income Documentation, Appraisal, Letter of Explanations, etc to support the exception request and compensating factors. If exception is approved, loan may be subject to exception-based pricing.",
        partnerName: 'Sky Port',
        emailDomain: 'skytpo.com',
        siteDomain: 'skytpo.com',
        loginVariant: LOGIN_VARIANTS.SKYTPO,
        labels: {
            feeText: 'Payable to Sky Port',
            whyChooseTitle: 'Why Choose Sky TPO?',
            copyRightText: 'Sky Port. All rights reserved.',
            nmlsText: 'NMLS ID: #1234567',
            welcomeText: 'Welcome to Sky TPO',
        },
        legacyBrandName: 'sky',
        contactSupportHelpText: 'How can we help you,  Sky TPO? Send us a message.',
    },
    ChampsTPO: {
        id: 'ChampsTPO',
        showHeaderLtv: false,
        logo: '/content/Images/Hero-Logo-Horizontal.png',
        layoutBackground: '/content/Images/HERO_BG_72dpi.png',
        favicon: '/favicon.png',
        theme: CHAMPS_THEME,
        loanPrograms: CHAMPS_LOAN_PROGRAMS,
        // ACTIVATOR PRIME stays visible (greyed out) so loans already on it
        // keep and save the value, but brokers can no longer select it. Such
        // loans are also blocked from Send/Request Disclosures and Submit Loan
        // - see the "Loan Program Not Available" group in requiredFields.json.
        disabledLoanPrograms: [LOAN_PROGRAM_CODES.PROGRAM_009],
        // Same block when field 1401 (Loan Program) contains one of these,
        // case-insensitive - e.g. "Activator Prime 30 Yr Fixed",
        // "Activator Prime 10/6 SOFR ARM", "Activator Prime 7/6 SOFR ARM".
        disabledLoanProgramNames: ['Activator Prime'],
        // See the SkyTPO note - ChampsTPO imports the value unchanged.
        clearImportedRentalIncomeWithoutReo: false,
        // FE0115 (Borr Self Employed) is a SkyTPO-only Additional Information
        // field - it is not exposed for Champs, so it is hidden here alongside
        // the existing '420' exclusion.
        hiddenAdditionalInfoFields: ['420', 'FE0115'],
        showAchCheckbox: true,
        exceptionLoanPrograms: CHAMPS_EXCEPTION_LOAN_PROGRAMS,
        guidelineMatrices: CHAMPS_GUIDELINE_MATRICES,
        // Rate-comparison sheet offered from Quick Price. See the SkyTPO note.
        quickPriceActivatorPrimeComparisonImage: '/content/Images/Activator_Prime_Comparison.png',
        // See the SkyTPO note - Champs does offer this exception path.
        showIneligibleExceptionNote: true,
        ineligibleMessage: 'There are no eligible products that match your search criteria.',
        companyName: 'Champions Funding',
        shortName: 'Champs',
        tpoName: 'ChampsTPO',
        allContacts: [
            {
                role: "Scenarios Desk",
                userName: "",
                email: "scenarios@champstpo.com",
                phone: "",
            },
            {
                role: "Client Approval",
                userName: "",
                email: "clientapproval@champstpo.com",
                phone: "",
            }
        ],
        allCorrContacts: [
            {
                role: "Scenarios Desk",
                userName: "",
                email: "corr.scenarios@champstpo.com",
                phone: "",
            },
            {
                role: "Client Support",
                userName: "",
                email: "corr.support@champstpo.com",
                phone: "",
            }
        ],
        emails: {
            clientApproval: 'clientapproval@champstpo.com',
        },
        links: {
            exceptionRequest: 'https://www.champstpo.com/exception-request',
            // Internal portal the logo points at for employee sessions.
            employeePortal: 'https://hero.champstpo.com',
        },
        channelSupport: {
            wholesale: {
                title: 'Wholesale Support',
                phone: '888-210-9881',
                email: 'scenarios@champstpo.com',
            },
            correspondent: {
                title: 'Correspondent Support',
                phone: '888-503-7677',
                email: 'corr.support@champstpo.com',
            },
        },
        exceptionReqMsg: "This will submit an Exception Request Form to Champs. You may be asked for the credit report and additional relevant documentation such as Asset Statements, Income Documentation, Appraisal, Letter of Explanations, etc to support the exception request and compensating factors. If exception is approved, loan may be subject to exception-based pricing.",
        partnerName: 'Mortgage Champ',
        emailDomain: 'champstpo.com',
        siteDomain: 'champstpo.com',
        loginVariant: LOGIN_VARIANTS.CHAMPSTPO,
        labels: {
            feeText: 'Payable to Champions Funding',
            whyChooseTitle: 'Why Choose Champions Funding?',
            copyRightText: 'Champions Funding. All rights reserved.',
            nmlsText: 'NMLS ID: #1234567',
            welcomeText: 'Welcome to Champions Funding',
        },
        legacyBrandName: 'Champs',
        contactSupportHelpText: 'How can we help you, Champions Funding? Send us a message.',
    }
};
export const DEFAULT_ORG_ID = LOGIN_VARIANTS.CHAMPSTPO;

// Organization-specific visibility for LTV in the loan header.
export const isHeaderLtvVisible = (organizationId = process.env.REACT_APP_ORGANIZATION) => {
    const activeId = (organizationId || DEFAULT_ORG_ID).toString().trim();
    const organization = Object.values(ORGANIZATIONS).find(
        org => org.id.toLowerCase() === activeId.toLowerCase()
    ) || ORGANIZATIONS[DEFAULT_ORG_ID];
    return organization.showHeaderLtv === true;
};

// Apply organization-specific field visibility before rendering, validating, or saving.
export const getAdditionalInfoFields = (fields, organizationId = process.env.REACT_APP_ORGANIZATION) => {
    const activeId = (organizationId || DEFAULT_ORG_ID).toString().trim();
    const organization = Object.values(ORGANIZATIONS).find(
        org => org.id.toLowerCase() === activeId.toLowerCase()
    ) || ORGANIZATIONS[DEFAULT_ORG_ID];
    const hiddenFields = new Set(organization.hiddenAdditionalInfoFields || []);
    return fields.filter(field => !hiddenFields.has(field.id));
};

/*
 * Loan-program lookups.
 *
 * These take the organization explicitly so they stay pure and testable. Most
 * callers want the versions in src/utils/loanProgramUtil.js, which bind the
 * org resolved from REACT_APP_ORGANIZATION.
 */

// Canonical code -> this org's Encompass value, or null when the org does not
// carry the product. Also the display name: the value shown to the user and
// the value stored in Encompass are the same string.
export const getLoanProgramValue = (organization, canonicalCode) =>
    organization?.loanPrograms?.[canonicalCode] || null;

// Every product this org actually carries, as dropdown-ready options. `code`
// is what the app stores in state and compares against; `value`/`text` are the
// org's brand name.
export const getLoanProgramOptions = (organization) =>
    Object.entries(organization?.loanPrograms || {})
        .filter(([, encompassValue]) => Boolean(encompassValue))
        .map(([canonicalCode, encompassValue]) => ({
            code: canonicalCode,
            value: encompassValue,
            text: encompassValue
        }));

// True when the org lists the product but brokers may not select it.
export const isLoanProgramDisabled = (organization, canonicalCode) =>
    (organization?.disabledLoanPrograms || []).includes(canonicalCode);

// True when a field 1401 Loan Program value contains one of the org's
// disabledLoanProgramNames (case-insensitive).
export const isLoanProgramNameDisabled = (organization, loanProgramName) => {
    const name = (loanProgramName || '').toString().trim().toUpperCase();
    if (!name) return false;

    return (organization?.disabledLoanProgramNames || []).some(
        (blocked) => name.includes(blocked.toString().trim().toUpperCase())
    );
};

/*
 * This org's Encompass value -> canonical code.
 *
 * Falls back to the normalized input when nothing matches, so a value written
 * before a product was registered here - or one belonging to another org -
 * still flows through the app unchanged rather than becoming empty. Callers
 * that need to distinguish "known product" from "unrecognized string" should
 * check the result against LOAN_PROGRAM_CODES.
 */
export const getCanonicalLoanProgram = (organization, encompassValue) => {
    const normalizedValue = (encompassValue || '').toString().trim().toUpperCase();

    return Object.entries(organization?.loanPrograms || {}).find(
        ([, value]) => value?.toString().trim().toUpperCase() === normalizedValue
    )?.[0] || normalizedValue;
};
